import "@baklavajs/themes/dist/syrup-dark.css"
import { createEffect, createMemo, createSignal, For, onCleanup, onMount, Show } from "solid-js"
import { Button } from "@openanalyst-ai/ui/button"
import { applyBaklavaHydration, toBaklavaHydrationModel } from "./visual-pipeline-baklava"
import type { RegistrySubmitDescriptor } from "./registry-submit-runtime"
import {
  addVisualPipelineEdge,
  addVisualPipelineNode,
  exportVisualPipelineTemplateBundle,
  removeVisualPipelineEdge,
  removeVisualPipelineNode,
  validateVisualPipelineAuthoringGraph,
} from "./visual-pipeline-authoring"

export type VisualPipelineGraph = {
  schemaVersion: 1
  nodes: Array<{
    id: string
    type: string
    label: string
    position: { x: number; y: number }
    data: Record<string, unknown>
  }>
  edges: Array<{ id: string; from: string; to: string; label?: string; data?: Record<string, unknown> }>
  viewport?: { x: number; y: number; zoom: number }
}

export type VisualPipelineBlock = {
  id: string
  type: "visual_pipeline"
  title?: string
  description?: string
  mode: "design"
  submitToolName: string
  graph: VisualPipelineGraph
  nodeTypes: Array<{
    type: string
    label: string
    description?: string
    category?: string
    defaultData?: Record<string, unknown>
    fields?: Array<Record<string, unknown>>
  }>
  templates?: Array<{ id: string; label: string; description?: string; graph: VisualPipelineGraph }>
  validation?: {
    status: "valid" | "warning" | "error"
    messages: Array<{ id: string; tone: "info" | "warning" | "error"; message: string; nodeId?: string; edgeId?: string }>
  }
  exportActions?: Array<{ command: "pipeline.export_markdown" | "pipeline.export_recipe"; label: string }>
}

type VisualPipelineCommandName = "pipeline.save" | "pipeline.export_markdown" | "pipeline.export_recipe"

export type VisualPipelineCommand = RegistrySubmitDescriptor & {
  command: VisualPipelineCommandName
}

export type VisualPipelineSubmitCommands = {
  save?: VisualPipelineCommand
  exports: VisualPipelineCommand[]
}

export function VisualPipelineBlockView(props: {
  block: VisualPipelineBlock
  pending: boolean
  readonly?: boolean
  submitCommands?: VisualPipelineSubmitCommands
  submitState?: { error?: string; result?: unknown }
  onSubmit: (blockId: string, values: Record<string, unknown>) => void | Promise<void>
}) {
  let host: HTMLDivElement | undefined
  let cleanup: (() => void) | undefined
  const [graph, setGraph] = createSignal(structuredClone(props.block.graph))
  const [undoStack, setUndoStack] = createSignal<VisualPipelineGraph[]>([])
  const [redoStack, setRedoStack] = createSignal<VisualPipelineGraph[]>([])
  const [dirty, setDirty] = createSignal(false)
  const [saveInvoked, setSaveInvoked] = createSignal(false)
  const [mountReady, setMountReady] = createSignal(false)
  const [visualUnavailableReason, setVisualUnavailableReason] = createSignal<string>()
  const [selectedNodeId, setSelectedNodeId] = createSignal(props.block.graph.nodes[0]?.id ?? "")
  const [edgeSourceId, setEdgeSourceId] = createSignal(props.block.graph.nodes[0]?.id ?? "")
  const [edgeTargetId, setEdgeTargetId] = createSignal(props.block.graph.nodes[1]?.id ?? props.block.graph.nodes[0]?.id ?? "")
  const [edgeError, setEdgeError] = createSignal("")
  const [templateId, setTemplateId] = createSignal(props.block.templates?.[0]?.id ?? "")
  const [templateJson, setTemplateJson] = createSignal("")
  const [hydrationResult, setHydrationResult] = createSignal({ warnings: [] as string[], nodeIds: [] as string[], edgeIds: [] as string[] })

  createEffect(() => {
    setGraph(structuredClone(props.block.graph))
    setDirty(false)
    setSaveInvoked(false)
    setUndoStack([])
    setRedoStack([])
    setSelectedNodeId(props.block.graph.nodes[0]?.id ?? "")
    setEdgeSourceId(props.block.graph.nodes[0]?.id ?? "")
    setEdgeTargetId(props.block.graph.nodes[1]?.id ?? props.block.graph.nodes[0]?.id ?? "")
    setEdgeError("")
    setTemplateId(props.block.templates?.[0]?.id ?? "")
    setTemplateJson("")
  })

  async function mountBaklava() {
    cleanup?.()
    cleanup = undefined
    if (!host) return

    const vue = await import("vue")
    const renderer = await import("@baklavajs/renderer-vue")
    const component = vue.defineComponent({
      setup() {
        const baklava = renderer.useBaklava()
        baklava.settings.palette.enabled = false
        baklava.settings.sidebar.enabled = false
        baklava.settings.toolbar.enabled = false
        baklava.settings.contextMenu.enabled = false
        baklava.settings.nodes.resizable = false
        setHydrationResult(applyBaklavaHydration({ editor: baklava.editor, model: hydrationModel(), readonly: true }))
        return () =>
          vue.h("div", { class: "h-full min-h-[520px] oa-visual-pipeline-canvas-readonly" }, [
            vue.h(renderer.BaklavaEditor, { viewModel: baklava }),
          ])
      },
    })
    const app = vue.createApp(component)
    app.mount(host)
    cleanup = () => app.unmount()
    setVisualUnavailableReason(undefined)
  }

  onMount(() => {
    setMountReady(true)
  })

  const baklavaSignature = createMemo(() =>
    `${props.block.id}:${graph()
      .nodes.map((node) => `${node.id}:${node.type}:${node.label}:${node.position.x},${node.position.y}`)
      .join("|")}:${graph()
      .edges.map((edge) => `${edge.id}:${edge.from}->${edge.to}:${edge.label ?? ""}`)
      .join("|")}`,
  )

  createEffect(() => {
    if (!mountReady()) return
    void baklavaSignature()
    void mountBaklava().catch((error) => {
      setVisualUnavailableReason(error instanceof Error ? error.message : "Visual canvas unavailable")
    })
  })

  onCleanup(() => {
    cleanup?.()
    cleanup = undefined
  })

  const hydrationModel = createMemo(() => toBaklavaHydrationModel(graph(), props.block.nodeTypes))
  const selectedNode = createMemo(() => graph().nodes.find((node) => node.id === selectedNodeId()) ?? graph().nodes[0])
  const legacySaveCommand = createMemo<VisualPipelineCommand>(() => ({
    id: "save",
    label: "Save",
    command: "pipeline.save",
    variant: "primary",
    disabled: false,
    disabledFor: () => false,
    pending: props.pending,
    run: (values) => props.onSubmit(props.block.id, values),
  }))
  const legacyExportCommands = createMemo<VisualPipelineCommand[]>(() =>
    (props.block.exportActions ?? []).map((action) => ({
      id: action.command,
      label: action.label,
      command: action.command,
      variant: "secondary",
      disabled: false,
      disabledFor: () => false,
      pending: props.pending,
      run: (values) => props.onSubmit(props.block.id, values),
    })),
  )
  const saveCommand = createMemo(() => (props.submitCommands ? props.submitCommands.save : legacySaveCommand()))
  const exportCommands = createMemo(() => (props.submitCommands ? props.submitCommands.exports : legacyExportCommands()))
  const canAuthor = createMemo(() => Boolean(!props.readonly && saveCommand()))
  const editable = createMemo(() => Boolean(canAuthor() && !props.pending))
  const groupedNodeTypes = createMemo(() =>
    Object.entries(
      props.block.nodeTypes.reduce<Record<string, VisualPipelineBlock["nodeTypes"]>>((groups, item) => {
        const key = item.category || "Pipeline"
        groups[key] = [...(groups[key] ?? []), item]
        return groups
      }, {}),
    ),
  )
  const authoringValidation = createMemo(() => validateVisualPipelineAuthoringGraph(graph(), props.block.nodeTypes))
  const validationMessages = createMemo(() => [...(props.block.validation?.messages ?? []), ...authoringValidation().messages])
  const selectedMessages = createMemo(() => validationMessages().filter((message) => message.nodeId === selectedNode()?.id))
  const submitError = createMemo(() => props.submitState?.error ?? saveCommand()?.error ?? exportCommands().find((command) => command.error)?.error)
  const submitResult = createMemo(() => props.submitState?.result ?? saveCommand()?.result ?? exportCommands().find((command) => command.result)?.result)
  const disabledReasons = createMemo(() =>
    [saveCommand(), ...exportCommands()].flatMap((command) =>
      command && commandConditionDisabled(command) && command.disabledReason ? [`${command.label}: ${command.disabledReason}`] : [],
    ),
  )

  createEffect(() => {
    const nodes = graph().nodes
    if (!nodes.some((node) => node.id === selectedNodeId())) setSelectedNodeId(nodes[0]?.id ?? "")
    if (!nodes.some((node) => node.id === edgeSourceId())) setEdgeSourceId(nodes[0]?.id ?? "")
    if (!nodes.some((node) => node.id === edgeTargetId())) setEdgeTargetId(nodes[1]?.id ?? nodes[0]?.id ?? "")
  })

  function commandConditionDisabled(command: VisualPipelineCommand) {
    return command.disabledFor(commandValues(command))
  }

  function commandDisabled(command: VisualPipelineCommand) {
    return Boolean(props.readonly || props.pending || command.pending || commandConditionDisabled(command))
  }

  function commandLabel(command: VisualPipelineCommand) {
    if (!props.pending && !command.pending) return command.label
    if (command.command === "pipeline.save") return "Saving..."
    if (command.command === "pipeline.export_markdown") return "Exporting Markdown..."
    return "Exporting Recipe..."
  }

  function submitResultText(result: unknown) {
    if (typeof result === "string") return result
    if (!result || typeof result !== "object" || Array.isArray(result)) return ""
    const record = result as Record<string, unknown>
    const value = record.path ?? record.exportPath ?? record.filePath ?? record.url ?? record.message
    return typeof value === "string" ? value : ""
  }

  function saveStatus() {
    if (!saveCommand()) return "Viewing"
    if (dirty()) return "Unsaved"
    if (saveInvoked()) return "Saved"
    return "Ready"
  }

  function commandValues(command: VisualPipelineCommand) {
    const pipelineId = props.block.id
    const currentGraph = graph()
    return {
      command: command.command,
      pipelineId,
      title: props.block.title,
      graph: currentGraph,
      payload: JSON.stringify({ pipelineId, title: props.block.title, graph: currentGraph }),
    }
  }

  function connectedLabels(nodeId: string, direction: "inputs" | "outputs") {
    return graph()
      .edges.filter((edge) => (direction === "inputs" ? edge.to === nodeId : edge.from === nodeId))
      .map((edge) => nodeLabel(direction === "inputs" ? edge.from : edge.to))
  }

  function roleFor(type: string) {
    return (
      {
        brief: "Defines the core request or campaign premise.",
        audience: "Identifies who the generated asset is for.",
        offer: "Defines the promise, hook, or call to action.",
        asset_slot: "Identifies reference material used by generation.",
        scene: "Turns strategy inputs into the creative generation prompt.",
        variant_set: "Controls how many creative variants should be produced.",
        output_pack: "Defines the output format or asset package.",
        review_gate: "Records criteria for human review before use.",
      } as Record<string, string>
    )[type] ?? "Describes one step in this visual pipeline."
  }

  function commitGraph(next: VisualPipelineGraph) {
    setUndoStack((current) => [...current.slice(-24), structuredClone(graph())])
    setRedoStack([])
    setGraph(structuredClone(next))
    setDirty(true)
  }

  function undoGraph() {
    const previous = undoStack().at(-1)
    if (!previous) return
    setRedoStack((current) => [structuredClone(graph()), ...current].slice(0, 25))
    setUndoStack((current) => current.slice(0, -1))
    setGraph(structuredClone(previous))
    setDirty(true)
    setEdgeError("")
  }

  function redoGraph() {
    const next = redoStack()[0]
    if (!next) return
    setUndoStack((current) => [...current.slice(-24), structuredClone(graph())])
    setRedoStack((current) => current.slice(1))
    setGraph(structuredClone(next))
    setDirty(true)
    setEdgeError("")
  }

  function save(command: VisualPipelineCommand) {
    if (commandDisabled(command)) return
    void command.run(commandValues(command))
    if (!props.submitCommands) {
      setDirty(false)
      setSaveInvoked(true)
    }
  }

  function exportGraph(command: VisualPipelineCommand) {
    if (commandDisabled(command)) return
    void command.run(commandValues(command))
  }

  function updateNodeData(nodeId: string, fieldKey: string, value: unknown) {
    if (!editable()) return
    setGraph((current) => ({
      ...current,
      nodes: current.nodes.map((node) =>
        node.id === nodeId
          ? {
              ...node,
              data: {
                ...node.data,
                [fieldKey]: value,
              },
            }
          : node,
      ),
    }))
    setDirty(true)
  }

  function fieldValue(nodeId: string, fieldKey: string) {
    return graph().nodes.find((node) => node.id === nodeId)?.data[fieldKey]
  }

  function nodeType(type: string) {
    return props.block.nodeTypes.find((item) => item.type === type)
  }

  function nodeLabel(nodeId: string) {
    return graph().nodes.find((node) => node.id === nodeId)?.label ?? nodeId
  }

  function addNode(type: VisualPipelineBlock["nodeTypes"][number]) {
    if (!editable()) return
    const next = addVisualPipelineNode(graph(), type).graph
    commitGraph(next)
    setSelectedNodeId(next.nodes.at(-1)?.id ?? selectedNodeId())
    setEdgeError("")
  }

  function deleteSelectedNode() {
    const node = selectedNode()
    if (!editable() || !node) return
    const next = removeVisualPipelineNode(graph(), node.id)
    commitGraph(next)
    setSelectedNodeId(next.nodes[0]?.id ?? "")
    setEdgeError("")
  }

  function addEdgeFromForm() {
    if (!editable()) return
    const result = addVisualPipelineEdge(graph(), { from: edgeSourceId(), to: edgeTargetId() })
    if (result.error) {
      setEdgeError(result.error)
      return
    }
    setEdgeError("")
    commitGraph(result.graph)
  }

  function deleteEdge(edgeId: string) {
    if (!editable()) return
    setEdgeError("")
    commitGraph(removeVisualPipelineEdge(graph(), edgeId))
  }

  function applyTemplate() {
    const template = props.block.templates?.find((item) => item.id === templateId())
    if (!editable() || !template) return
    commitGraph(template.graph)
    setSelectedNodeId(template.graph.nodes[0]?.id ?? "")
    setTemplateJson("")
    setEdgeError("")
  }

  function exportTemplateJson() {
    const bundle = exportVisualPipelineTemplateBundle({
      id: `${props.block.id}-template`,
      label: props.block.title ?? props.block.id,
      graph: graph(),
    })
    setTemplateJson(JSON.stringify(bundle, null, 2))
  }

  function fieldOptions(field: Record<string, unknown>) {
    return Array.isArray(field.options) ? field.options : []
  }

  function updateFieldFromInput(nodeId: string, field: Record<string, unknown>, rawValue: string | boolean) {
    if (field.type === "toggle") {
      updateNodeData(nodeId, String(field.key), Boolean(rawValue))
      return
    }
    if (field.type === "number") {
      if (rawValue === "") {
        updateNodeData(nodeId, String(field.key), "")
        return
      }
      const parsed = Number(rawValue)
      if (Number.isFinite(parsed)) updateNodeData(nodeId, String(field.key), parsed)
      return
    }
    updateNodeData(nodeId, String(field.key), rawValue)
  }

  return (
    <div data-visual-pipeline-block={props.block.id} class="flex min-h-0 flex-col gap-3">
      <div class="flex flex-wrap items-center gap-2">
        <Show when={saveCommand()}>
          {(command) => (
            <Button type="button" size="small" variant="primary" disabled={commandDisabled(command())} onClick={() => save(command())}>
              {commandLabel(command())}
            </Button>
          )}
        </Show>
        <For each={exportCommands()}>
          {(command) => (
            <Button
              type="button"
              size="small"
              variant="secondary"
              disabled={commandDisabled(command)}
              onClick={() => exportGraph(command)}
            >
              {commandLabel(command)}
            </Button>
          )}
        </For>
        <span class="text-12-medium text-text-base">
          {saveStatus()} / {props.block.validation?.status ?? "unchecked"}
        </span>
      </div>
      <Show when={disabledReasons().length}>
        <div data-visual-pipeline-disabled-reasons class="flex flex-col gap-1 text-12-regular text-text-base">
          <For each={disabledReasons()}>{(reason) => <div>{reason}</div>}</For>
        </div>
      </Show>
      <Show when={submitError()}>
        {(message) => (
          <div
            role="status"
            data-visual-pipeline-submit-error
            class="rounded-md border border-border-weak-base bg-background-secondary px-3 py-2 text-12-regular text-text-base"
          >
            {message()}
          </div>
        )}
      </Show>
      <Show when={submitResultText(submitResult())}>
        {(message) => (
          <div
            role="status"
            data-visual-pipeline-submit-result
            class="rounded-md border border-border-weak-base bg-background-secondary px-3 py-2 text-12-regular text-text-base"
          >
            {message()}
          </div>
        )}
      </Show>
      <Show when={canAuthor()}>
        <div data-visual-pipeline-authoring class="grid grid-cols-1 gap-3 lg:grid-cols-[220px_minmax(0,1fr)]">
          <div class="rounded-md border border-border-weak-base bg-background-secondary p-3">
            <div class="text-12-medium text-text-strong">Add node</div>
            <For each={groupedNodeTypes()}>
              {([category, items]) => (
                <div class="mt-3">
                  <div class="text-11-regular text-text-base">{category}</div>
                  <div class="mt-2 flex flex-col gap-2">
                    <For each={items}>
                      {(item) => (
                        <Button type="button" size="small" variant="secondary" disabled={!editable()} onClick={() => addNode(item)}>
                          <span data-visual-pipeline-add-node={item.type}>Add {item.label}</span>
                        </Button>
                      )}
                    </For>
                  </div>
                </div>
              )}
            </For>
          </div>
          <div class="flex min-w-0 flex-col gap-3">
            <div class="flex flex-wrap items-center gap-2 rounded-md border border-border-weak-base bg-background-secondary p-3">
              <Button type="button" size="small" variant="secondary" disabled={!editable() || !undoStack().length} onClick={undoGraph}>
                Undo
              </Button>
              <Button type="button" size="small" variant="secondary" disabled={!editable() || !redoStack().length} onClick={redoGraph}>
                Redo
              </Button>
              <Button type="button" size="small" variant="secondary" disabled={!editable() || !selectedNode()} onClick={deleteSelectedNode}>
                Delete node
              </Button>
            </div>
            <Show when={props.block.templates?.length}>
              <div class="rounded-md border border-border-weak-base bg-background-secondary p-3">
                <div class="text-12-medium text-text-strong">Templates</div>
                <div class="mt-2 grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto_auto]">
                  <select
                    data-visual-pipeline-template-select
                    class="rounded-md border border-border-weak-base bg-background-base px-2 py-1 text-12-regular text-text-strong"
                    value={templateId()}
                    disabled={!editable()}
                    onChange={(event) => setTemplateId(event.currentTarget.value)}
                  >
                    <For each={props.block.templates ?? []}>{(template) => <option value={template.id}>{template.label}</option>}</For>
                  </select>
                  <Button type="button" size="small" variant="secondary" disabled={!editable() || !templateId()} onClick={applyTemplate}>
                    Apply template
                  </Button>
                  <Button type="button" size="small" variant="secondary" disabled={!editable()} onClick={exportTemplateJson}>
                    Export Template JSON
                  </Button>
                </div>
                <Show when={templateJson()}>
                  <pre
                    data-visual-pipeline-template-json
                    class="mt-2 max-h-48 overflow-auto rounded-md border border-border-weak-base bg-background-base p-2 text-11-regular text-text-base"
                  >
                    {templateJson()}
                  </pre>
                </Show>
              </div>
            </Show>
          </div>
        </div>
      </Show>
      <Show when={validationMessages().length}>
        <div class="rounded-md border border-border-weak-base bg-background-secondary px-3 py-2 text-12-regular text-text-base">
          <For each={validationMessages()}>{(message) => <div>{message.message}</div>}</For>
        </div>
      </Show>
      <Show when={visualUnavailableReason()}>
        <div class="rounded-md border border-border-weak-base bg-background-secondary px-3 py-2 text-12-regular text-text-base">
          {visualUnavailableReason()}
        </div>
      </Show>
      <div
        ref={host}
        class="min-h-[520px] overflow-hidden rounded-lg border border-border-weak-base bg-background-base"
        data-visual-pipeline-canvas={props.block.id}
        data-hydrated-nodes={hydrationResult().nodeIds.length}
        data-hydrated-edges={hydrationResult().edgeIds.length}
      />
      <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
        <For each={graph().nodes}>
          {(node) => {
            const fields = () => nodeType(node.type)?.fields ?? []
            return (
              <div
                class={`flex flex-col gap-3 rounded-md border p-3 text-left ${
                  selectedNode()?.id === node.id ? "border-border-strong bg-background-base" : "border-border-weak-base bg-background-secondary"
                }`}
                data-visual-pipeline-node={node.id}
              >
                <button
                  type="button"
                  class="text-left"
                  data-visual-pipeline-select-node={node.id}
                  onClick={() => setSelectedNodeId(node.id)}
                >
                  <div class="text-13-medium text-text-strong">{node.label}</div>
                  <div class="mt-1 text-11-regular text-text-base">{node.type}</div>
                </button>
                <For each={fields()}>
                  {(field) => {
                    const fieldKey = String(field.key ?? "")
                    const name = `${node.id}.${fieldKey}`
                    return (
                      <Show when={fieldKey}>
                        <label class="flex flex-col gap-1 text-12-regular text-text-base">
                          <span>{String(field.label ?? fieldKey)}</span>
                          <Show
                            when={field.type === "toggle"}
                            fallback={
                              <Show
                                when={field.type === "textarea"}
                                fallback={
                                  <Show
                                    when={field.type === "select"}
                                    fallback={
                                      <input
                                        class="rounded-md border border-border-weak-base bg-background-base px-2 py-1 text-12-regular text-text-strong"
                                        data-visual-pipeline-field={name}
                                        name={name}
                                        type={field.type === "number" ? "number" : "text"}
                                        value={String(fieldValue(node.id, fieldKey) ?? "")}
                                        disabled={!editable()}
                                        onInput={(event) => updateFieldFromInput(node.id, field, event.currentTarget.value)}
                                      />
                                    }
                                  >
                                    <select
                                      class="rounded-md border border-border-weak-base bg-background-base px-2 py-1 text-12-regular text-text-strong"
                                      data-visual-pipeline-field={name}
                                      name={name}
                                      value={String(fieldValue(node.id, fieldKey) ?? "")}
                                      disabled={!editable()}
                                      onChange={(event) => updateFieldFromInput(node.id, field, event.currentTarget.value)}
                                    >
                                      <For each={fieldOptions(field)}>
                                        {(option) => {
                                          const value =
                                            typeof option === "string"
                                              ? option
                                              : typeof option === "object" && option !== null && "value" in option
                                                ? String(option.value)
                                                : ""
                                          const label =
                                            typeof option === "object" && option !== null && "label" in option ? String(option.label) : value
                                          return <option value={value}>{label}</option>
                                        }}
                                      </For>
                                    </select>
                                  </Show>
                                }
                              >
                                <textarea
                                  class="min-h-20 rounded-md border border-border-weak-base bg-background-base px-2 py-1 text-12-regular text-text-strong"
                                  data-visual-pipeline-field={name}
                                  name={name}
                                  value={String(fieldValue(node.id, fieldKey) ?? "")}
                                  disabled={!editable()}
                                  onInput={(event) => updateFieldFromInput(node.id, field, event.currentTarget.value)}
                                />
                              </Show>
                            }
                          >
                            <input
                              class="h-4 w-4"
                              data-visual-pipeline-field={name}
                              name={name}
                              type="checkbox"
                              checked={Boolean(fieldValue(node.id, fieldKey))}
                              disabled={!editable()}
                              onChange={(event) => updateFieldFromInput(node.id, field, event.currentTarget.checked)}
                            />
                          </Show>
                        </label>
                      </Show>
                    )
                  }}
                </For>
              </div>
            )
          }}
        </For>
      </div>
      <Show when={selectedNode()}>
        {(node) => (
          <div data-visual-pipeline-inspector class="rounded-md border border-border-weak-base bg-background-secondary p-3">
            <div class="text-13-medium text-text-strong">{node().label}</div>
            <div class="mt-1 text-11-regular text-text-base">{node().type}</div>
            <div class="mt-3 text-12-regular text-text-base">{roleFor(node().type)}</div>
            <div class="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
              <div>
                <div class="text-12-medium text-text-strong">Inputs</div>
                <For each={connectedLabels(node().id, "inputs")} fallback={<div class="text-12-regular text-text-base">None</div>}>
                  {(label) => <div class="text-12-regular text-text-base">{label}</div>}
                </For>
              </div>
              <div>
                <div class="text-12-medium text-text-strong">Outputs</div>
                <For each={connectedLabels(node().id, "outputs")} fallback={<div class="text-12-regular text-text-base">None</div>}>
                  {(label) => <div class="text-12-regular text-text-base">{label}</div>}
                </For>
              </div>
            </div>
            <Show when={selectedMessages().length}>
              <div class="mt-3 flex flex-col gap-1">
                <For each={selectedMessages()}>{(message) => <div class="text-12-regular text-text-base">{message.message}</div>}</For>
              </div>
            </Show>
          </div>
        )}
      </Show>
      <div class="flex flex-col gap-2 rounded-md border border-border-weak-base bg-background-secondary p-3">
        <div class="text-12-medium text-text-strong">Connections</div>
        <Show when={canAuthor()}>
          <div class="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_auto]">
            <select
              data-visual-pipeline-edge-source
              class="rounded-md border border-border-weak-base bg-background-base px-2 py-1 text-12-regular text-text-strong"
              value={edgeSourceId()}
              disabled={!editable()}
              onChange={(event) => setEdgeSourceId(event.currentTarget.value)}
            >
              <For each={graph().nodes}>{(node) => <option value={node.id}>{node.label}</option>}</For>
            </select>
            <select
              data-visual-pipeline-edge-target
              class="rounded-md border border-border-weak-base bg-background-base px-2 py-1 text-12-regular text-text-strong"
              value={edgeTargetId()}
              disabled={!editable()}
              onChange={(event) => setEdgeTargetId(event.currentTarget.value)}
            >
              <For each={graph().nodes}>{(node) => <option value={node.id}>{node.label}</option>}</For>
            </select>
            <Button type="button" size="small" variant="secondary" disabled={!editable() || graph().nodes.length < 2} onClick={addEdgeFromForm}>
              Add edge
            </Button>
          </div>
          <Show when={edgeError()}>
            <div data-visual-pipeline-edge-error class="text-12-regular text-text-base">{edgeError()}</div>
          </Show>
        </Show>
        <Show when={graph().edges.length}>
          <For each={graph().edges}>
            {(edge) => (
              <div class="flex items-center justify-between gap-2 text-12-regular text-text-base" data-visual-pipeline-edge={edge.id}>
                <span>
                  {nodeLabel(edge.from)} -&gt; {nodeLabel(edge.to)}
                </span>
                <Show when={canAuthor()}>
                  <Button type="button" size="small" variant="secondary" disabled={!editable()} onClick={() => deleteEdge(edge.id)}>
                    <span data-visual-pipeline-delete-edge={edge.id}>Delete edge</span>
                  </Button>
                </Show>
              </div>
            )}
          </For>
        </Show>
      </div>
    </div>
  )
}
